import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Linkedin, Youtube, Bot } from 'lucide-react';
import OptimizedImage from '@/components/ui/OptimizedImage';
import logoFull from '@/assets/logo-full.png';

const Footer = () => {
  const services = [
    { name: 'iPhone Repair', path: '/iphone-service' },
    { name: 'MacBook Repair', path: '/macbook-service' },
    { name: 'iPad Repair', path: '/ipad-service' },
    { name: 'Apple Watch Repair', path: '/watch-service' },
  ];

  const resources = [
    { name: 'Repair Blog', path: '/blog' },
    { name: 'iPhone Battery Guide', path: '/blog/iphone-battery-draining-fast-fix-patna' },
    { name: 'iPhone Charging Issues', path: '/blog/iphone-not-charging-solutions-bihar' },
    { name: 'Water Damage Recovery', path: '/blog/iphone-water-damage-recovery-patna' },
  ];

  const company = [
    { name: 'About Us', path: '/#about' },
    { name: 'Service Areas', path: '/service-areas' },
    { name: 'Franchise', path: '/franchise' },
    { name: 'Current Updates', path: '/updates' },
    { name: 'Contact', path: '/#contact' },
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/iFixapplepatna/', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/ifixapplepatna/', label: 'Instagram' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/ifixappleaptna/', label: 'LinkedIn' },
    { icon: Youtube, href: 'https://www.youtube.com/@ifixapplepatna111', label: 'YouTube' },
  ];

  return (
    <footer className="bg-foreground text-background">
      <div className="container-apple section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <OptimizedImage 
                src={logoFull} 
                alt="iFix Apple Care - Bihar's Trusted Apple Service Center for iPhone MacBook iPad Watch Repair in Patna" 
                className="h-40 w-auto object-contain"
              />
            </div>
            <p className="text-background/70 text-sm leading-relaxed mb-6">
              Bihar's trusted <Link to="/" className="text-background hover:underline">Apple device service center</Link>. Expert repairs for{' '}
              <Link to="/iphone-service" className="text-background hover:underline">iPhone</Link>,{' '}
              <Link to="/macbook-service" className="text-background hover:underline">MacBook</Link>,{' '}
              <Link to="/ipad-service" className="text-background hover:underline">iPad</Link>, and{' '}
              <Link to="/watch-service" className="text-background hover:underline">Apple Watch</Link> with genuine parts and certified technicians.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-background/20 transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    className="text-background/70 hover:text-background transition-colors text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold mb-6">Resources</h4>
            <ul className="space-y-3">
              {resources.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="text-background/70 hover:text-background transition-colors text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold mb-6">Company</h4>
            <ul className="space-y-3">
              {company.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="text-background/70 hover:text-background transition-colors text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <MapPin className="w-5 h-5 text-background/60 flex-shrink-0 mt-0.5" />
                <a
                  href="https://maps.app.goo.gl/i2KhEM81U68p5wrU8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-background/70 hover:text-background transition-colors text-sm"
                >
                  Shop No. 6, Jassinandan Plaza, New Dak Bunglow Rd, Patna, Bihar 800001
                </a>
              </li>
              <li className="flex gap-3">
                <Phone className="w-5 h-5 text-background/60 flex-shrink-0" />
                <a
                  href="tel:9798653591"
                  className="text-background/70 hover:text-background transition-colors text-sm"
                >
                  +91 97986 53591
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="w-5 h-5 text-background/60 flex-shrink-0" />
                <a
                  href="mailto:contact@ifixappleservicecenter.in"
                  className="text-background/70 hover:text-background transition-colors text-sm"
                >
                  contact@ifixappleservicecenter.in
                </a>
              </li>
              <li className="flex gap-3">
                <Clock className="w-5 h-5 text-background/60 flex-shrink-0 mt-0.5" />
                <div className="text-background/70 text-sm">
                  <p>Mon - Sat: 10:00 AM - 8:00 PM</p>
                  <p>Sunday: 11:00 AM - 5:00 PM</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-background/10">
          <div className="flex flex-col gap-6">
            <p className="text-background/40 text-xs text-center leading-relaxed max-w-4xl mx-auto">
              Apple Logos, Symbols, iPhone, iPad, MacBook and iMac are registered trademarks of Apple, Inc. registered in the U.S. and other countries. iFix is an independent service company and is in no way affiliated with Apple Inc.
            </p>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-background/50 text-sm text-center md:text-left">
                © {new Date().getFullYear()} iFix Apple Care. All rights reserved.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="/llms.txt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-background/50 hover:text-background/70 transition-colors text-xs flex items-center gap-1"
                  title="LLMs.txt - AI-readable site information"
                >
                  <Bot className="w-3 h-3" />
                  llms.txt
                </a>
                <p className="text-background/50 text-xs text-center">
                  Apple Service Center in Patna, Bihar | iPhone, MacBook, iPad & Apple Watch Repair
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
